import { computed, inject, Injectable, Signal } from '@angular/core';
import { QueryClient, queryOptions, UndefinedInitialDataOptions } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { CoreApiService, queryAll } from './core-api.service';
import { ProjectPermissions } from './projects/projects.service';
import { lockedProjectProhibitedActions, PermissionAction } from './shared/enums/permission-action.enum';
import { SiteRole } from './shared/enums/site-role.enum';
import { ProfileQueryService } from './profile-query.service';
import { Project } from './shared/models/project.model';
import { ProjectsQueryService } from './projects/projects-query.service';

export type PermissionActionString = `${PermissionAction}`;

@Injectable({
  providedIn: 'root',
})
export class PermissionQueryService {
  private coreApi = inject(CoreApiService);
  private queryClient = inject(QueryClient);
  private projectQueryService = inject(ProjectsQueryService);
  private profileQueryService = inject(ProfileQueryService);

  private projectPermissionsQuery(): UndefinedInitialDataOptions<ProjectPermissions> {
    return queryOptions({
      queryKey: ['self', 'api-permissions'],
      queryFn: () => lastValueFrom(this.coreApi.permissions.self()),
      placeholderData: {} as ProjectPermissions,
      refetchOnMount: false,
    });
  }

  isSiteAdmin = computed(async () => {
    const profile = await this.queryClient.ensureQueryData(this.profileQueryService.getProfile());
    return SiteRole.isSiteAdmin(profile);
  });

  private lockedProjectsQuery(): UndefinedInitialDataOptions<Project[]> {
    return queryOptions({
      queryKey: ['projects', 'locked'],
      queryFn: () => lastValueFrom(queryAll(this.coreApi.projects.query, { filter: 'locked!=null' })),
    });
  }

  async isProjectLocked(projectId: string): Promise<boolean> {
    const lockedProjects: Project[] = await this.queryClient.ensureQueryData(
      this.lockedProjectsQuery()
    );
    return lockedProjects.some(p => p._id === projectId);
  }

  projectPermissions(): UndefinedInitialDataOptions<ProjectPermissions> {
    // Previous implementations did NOT pre-fill permissions to give admins access to all projects here
    // however, this does grant full permissions to projects admins have any explicit permissions on.
    const allActions = Object.values(PermissionAction);
    const allLockedActions = allActions.filter(action => !lockedProjectProhibitedActions.has(action));
    return queryOptions({
      queryKey: ['self', 'permissions'],
      queryFn: async () => {
        const permissionsFromApi = await this.queryClient.ensureQueryData(this.projectPermissionsQuery());
        const siteAdmin = await this.isSiteAdmin();
        const lockedProjects = await this.queryClient.ensureQueryData(this.lockedProjectsQuery());
        for (const projectId in permissionsFromApi) {
          const projectLocked = lockedProjects.find(p => p._id === projectId);
          // reduce maximum permissions if a project is locked, even for site admins
          if (projectLocked && !siteAdmin) {
            permissionsFromApi[projectId] = permissionsFromApi[projectId].filter(permission => !lockedProjectProhibitedActions.has(permission));
          }
          else if (siteAdmin) {
            permissionsFromApi[projectId] = projectLocked ? allLockedActions : allActions;
          }
        }
        return permissionsFromApi;
      },
    });
  }

  // TODO: evaluate if this is useful or should be based off projectPermissions
  permissionsForProject(projectId: Signal<string>): UndefinedInitialDataOptions<PermissionActionString[]> {
    const allActions = Object.values(PermissionAction);
    // reduce maximum permissions if a project is locked, even for site admins
    const allLockedActions = allActions.filter(action => !lockedProjectProhibitedActions.has(action));

    return queryOptions({
      queryKey: ['projects', projectId(), 'permissions'],
      queryFn: async () => {

        if (!projectId) {
          return [];
        }

        const permissionsData = await this.queryClient.ensureQueryData(this.projectPermissions());
        const grantedPermissions = permissionsData[projectId()] ?? [];

        const lockedProjects = await this.queryClient.ensureQueryData(this.projectQueryService.allLockedProjects());
        const isProjectLocked = (projectId: string): boolean => Boolean(lockedProjects.results.find(project => project._id === projectId)?.locked)
        const siteAdmin = await this.isSiteAdmin();

        if (siteAdmin) {
          return isProjectLocked(projectId()) ? allLockedActions : allActions;
        }
        else if (isProjectLocked(projectId())) {
          return grantedPermissions.filter(permission => !lockedProjectProhibitedActions.has(permission));
        }
        return grantedPermissions;
      },
    });
  }

  invalidatePermissionsCache(): void {
    // invalide ['self', 'api-permissions'], ['self', 'permissions'], and ['projects', <page:xyz>, 'permissions']
    this.queryClient.invalidateQueries({
      predicate: query =>
        (query.queryKey[0] === 'self' && (query.queryKey[1] === 'api-permissions' || query.queryKey[1] === 'permissions')) ||
        (query.queryKey[0] === 'projects' && query.queryKey[2] === 'permissions'),
    });
  }

}
